/* eslint-disable react/forbid-prop-types */
import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { grey } from '@mui/material/colors';
import Grid from '../../../../components/Grid/Grid';
import Typography from '../../../../components/Typography/Typography';
import ReadonlyField from '../../../../components/ReadonlyField/ReadonlyField';
import Button from '../../../../components/Button/Button';
import { DialogContext } from '../../../../contexts/DialogContext';
import { dialogActions } from '../../../../reducers/dialogReducer';
import { calculateCPK, ksNormalTest } from '../../../../Utils/cpkUtil';
import { getCalculatedSpecConfig } from '../../../../Utils/measureSpecUtils';

/**
 * QCSinteringSizeCpkDialog
 * 顯示燒結尺寸 CPK 計算結果的對話框元件。
 *
 * @component
 * @param {Object} props - 組件參數
 * @param {Function} props.hancleClose - 關閉對話框的函數
 * @param {Function} props.handleSave - 儲存資料的函數，需接收 (rowData, { cpk, passed }) 兩個參數
 * @param {Array<Object>} props.rowData - 表格資料，每一列為一個量測結果
 * @param {number} props.rowData[].rowId - 行唯一識別碼
 * @param {number} props.rowData[].sinterRowId - 燒結行ID
 * @param {number} props.rowData[].sinterCheckId - 檢查ID
 * @param {Array<Object>} props.rowData[].specItems - 量測規格資料
 * @param {string} props.rowData[].specItems[].specId - 規格唯一識別碼，例如 "1960"
 * @param {string} props.rowData[].specItems[].specItem - 規格項目名稱，例如 "A"
 * @param {string} props.rowData[].specItems[].value - 量測值，例如 "16.03"
 * @param {number} props.rowData[].specItems[].detailId - 詳細ID
 * @param {number} props.rowData[].specItems[].sinterId - 燒結ID
 * @param {string} props.rowData[].specItems[].specRecipe - 規格配方
 * @param {number} props.rowData[].specItems[].colStatus - 欄位狀態，例如 2
 * @param {Array<Object>} props.rowData[].defectType - defectType陣列
 * @param {string} props.rowData[].defectType[].name - defectType代碼
 * @param {string} props.rowData[].defectType[].label - defectType名稱
 * @param {boolean} props.rowData[].defectType[].checked - 是否勾選
 * @param {boolean} props.rowData[].isInvalid - 是否為無效資料
 * @param {boolean} props.rowData[].isEmpty - 是否為空資料
 * @param {boolean} props.rowData[].isOutSpec - 是否超出規格
 * @param {Array<Object>} props.specConfig - 規格設定陣列，每一項為一個規格設定物件
 * @param {string} props.specConfig[].specId - 規格唯一識別碼，例如 "1960"
 * @param {string} props.specConfig[].specItem - 規格項目名稱，例如 "A"
 * @param {string} props.specConfig[].specTarget - 目標值，例如 "16"
 * @param {string} props.specConfig[].specUpper - 上限值，例如 "0.4"
 * @param {string} props.specConfig[].specLower - 下限值，例如 "0.4"
 * @param {string} props.specConfig[].specBias - 偏差值
 * @param {string} props.specConfig[].limitUpper - 規格上限
 * @param {string} props.specConfig[].limitLower - 規格下限
 * @param {string} props.specConfig[].specRecipe - 規格配方
 * @param {number} props.specConfig[].styleId - 規格類型ID，例如 1
 * @param {string} props.specConfig[].styleName - 規格類型名稱，例如 "尺寸"
 * @param {boolean} props.specConfig[].isEnabled - 是否啟用
 * @param {Array} props.specConfig[].measureConfig - 量測設定
 * @param {boolean} props.specConfig[].isActive - 是否為啟用狀態
 * @param {number} props.specConfig[].colStatus - 欄位狀態，例如 2
 * @returns {JSX.Element} CPK 計算結果對話框
 *
 * @example
 * const rowData = [
 *   {
 *     rowId: 0,
 *     sinterRowId: 2283827,
 *     sinterCheckId: 31911,
 *     specItems: [
 *       {
 *         specId: "1960",
 *         specItem: "A",
 *         value: "16.03",
 *         detailId: 6311548,
 *         sinterId: 2283827,
 *         specRecipe: "",
 *         colStatus: 2
 *       },
 *       // ...
 *     ],
 *     defectType: [
 *       { name: "1", label: "前缺", checked: false },
 *       // ...
 *     ],
 *     isInvalid: false,
 *     isEmpty: false,
 *     isOutSpec: false
 *   },
 *   // ...
 * ];
 * const specConfig = [
 *   {
 *     specId: "1960",
 *     specItem: "A",
 *     specTarget: "16",
 *     specUpper: "0.4",
 *     specLower: "0.4",
 *     specBias: "",
 *     limitUpper: "",
 *     limitLower: "",
 *     specRecipe: "",
 *     styleId: 1,
 *     styleName: "尺寸",
 *     isEnabled: true,
 *     measureConfig: [],
 *     isActive: true,
 *     colStatus: 2
 *   },
 *   // ...
 * ];
 * <QCSinteringSizeCpkDialog rowData={rowData} specConfig={specConfig} />
 */
const QCSinteringSizeCpkDialog = ({ rowData, specConfig, hancleClose, handleSave }) => {
  const { dialogDispatch } = useContext(DialogContext);
  const filteredSpecConfig = specConfig.filter((item) => item.isActive && item.isEnabled);
  const filteredSpecIdSet = new Set(filteredSpecConfig.map((item) => item.specId));
  const filteredRowData = rowData.filter(
    (item) =>
      !item.isEmpty &&
      !item.isInvalid &&
      item.specItems.some((specItem) => filteredSpecIdSet.has(specItem.specId))
  );

  const cpkResult = useMemo(() => {
    const newCpkResult = {};
    filteredSpecConfig.forEach((spec) => {
      const specData = [];
      filteredRowData.forEach((item) => {
        item.specItems.forEach((specItem) => {
          if (specItem.specId === spec.specId) {
            specData.push(Number(specItem.value));
          }
        });
      });
      const { specUpper, specLower } = getCalculatedSpecConfig(spec);
      let cpk = null;
      let isNormalDist = null;
      let isDataConstant = false;
      try {
        const { CPK, isConstant } = calculateCPK(specData, specUpper, specLower);
        cpk = CPK.toFixed(6);
        isDataConstant = isConstant;
        isNormalDist = ksNormalTest(specData).passed;
      } catch (e) {
        cpk = null;
        isNormalDist = null;
        isDataConstant = false;
      }
      newCpkResult[spec.specId] = { cpk, isNormalDist, isDataConstant };
    });
    return newCpkResult;
  }, [filteredSpecConfig, filteredRowData]);

  const handleCancel = () => {
    hancleClose();
    dialogDispatch({
      type: dialogActions.CLOSE,
    });
  };

  const handleConfirm = () => {
    dialogDispatch({
      type: dialogActions.CLOSE,
    });
    handleSave(rowData, cpkResult);
  };

  return (
    <Grid container spacing={3} sx={{ mb: 0.1 }}>
      <Grid item xs={12}>
        <Typography variant="body1">此次量測數據cpk計算結果如下：</Typography>
      </Grid>
      <Grid item xs={12} container columnSpacing={3}>
        {filteredSpecConfig.map((spec) => (
          <Grid item xs={4} key={spec.specId}>
            <ReadonlyField
              label={spec.specItem}
              name={spec.spaceName}
              value={cpkResult[spec.specId].cpk}
              helperText={cpkResult[spec.specId].isNormalDist ? '符合常態分布' : '不符合常態分布'}
            />
            {cpkResult[spec.specId].isDataConstant && (
              <Typography variant="caption" color="error" sx={{ lineHeight: 1.2 }}>
                資料異常，CPK結果可能不準確
              </Typography>
            )}
          </Grid>
        ))}
      </Grid>
      <Grid item container xs={12} spacing={1.5} justifyContent="end" sx={{ pr: 1.5 }}>
        <Grid>
          <Button
            variant="contained"
            color="grey"
            style={{ color: grey[50] }}
            sx={{ bgcolor: grey[400] }}
            onClick={handleCancel}
          >
            取消
          </Button>
        </Grid>
        <Grid>
          <Button variant="contained" color="primary" onClick={handleConfirm}>
            確認
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

QCSinteringSizeCpkDialog.propTypes = {
  rowData: PropTypes.array.isRequired,
  specConfig: PropTypes.array.isRequired,
  hancleClose: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
};

export default QCSinteringSizeCpkDialog;
